// src/utils/scrollLock.js
/**
 * Reference-counted body scroll lock — CSS-class based, not inline-style based.
 *
 * PageSpinner/ConfirmDialog aren't the only thing that can lock body scroll:
 * MUI's own <Dialog>/<Modal> (e.g. the Form 9 "has your patent been granted?"
 * dialog) independently sets `document.body.style.overflow = 'hidden'` while
 * open and restores it on close. Snapshotting/restoring that same inline
 * style from our own code is fragile — if our lock opens while MUI's is
 * still mid-close (its exit transition is still running), we capture
 * MUI's *temporary* 'hidden' value as "the original", and when we later
 * restore it we clobber MUI's already-correct restore, leaving the page
 * permanently unscrollable with nothing visibly open. This bit us exactly
 * when the Form 9 gate dialog closes and a PageSpinner opens right after it,
 * outliving the dialog's ~225ms close transition.
 *
 * Fixing it by owning a dedicated class instead of the inline style means we
 * never read or write the property MUI manages — both can be "hidden"
 * at once with no cross-contamination, and when we let go we only ever
 * remove *our own* contribution.
 *
 * Usage:
 *   useEffect(() => {
 *     if (!open) return;
 *     return lockBodyScroll();
 *   }, [open]);
 */
const LOCK_CLASS = 'app-scroll-locked';
let lockCount = 0;

export function lockBodyScroll() {
  lockCount += 1;
  document.body.classList.add(LOCK_CLASS);

  let released = false;
  return function unlock() {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove(LOCK_CLASS);
    }
  };
}
